import debugMessage from '../helpers/debugMessage';
import isMobile from '../helpers/isMobile';

const DEVICE_TYPE_DESKTOP = 'desktop';
const DEVICE_TYPE_MOBILE = 'mobile';
const PLATFORM_WEB = 'web';

const DEBUG_NAMESPACE = 'metrics';

const RETRY_ATTEMPTS = 3;
const THROTTLING_THRESHOLD = 500;

type TMetrics = Record<string, number>;

export type MetricsClientConstructorParams = {
  virtualUrl: string;
  endpointUrl: string;
  metricsSuffix?: string;
};

export default class MetricsClient {
  private pageLoaded = false;

  private queue: TMetrics[] = [];

  private throttlingTimer: ReturnType<typeof setTimeout> | undefined;

  private endpointUrl: string;
  private virtualUrl: string;
  private metricsSuffix: string;

  constructor({ virtualUrl, endpointUrl, metricsSuffix = '' }: MetricsClientConstructorParams) {
    this.virtualUrl = virtualUrl;
    this.endpointUrl = endpointUrl;
    this.metricsSuffix = metricsSuffix;
    if (document.readyState === 'complete') {
      this.onPageLoaded();
    } else {
      window.addEventListener('load', this.onPageLoaded);
    }
  }

  /**
   * Used for tracking performance
   *
   * @param label - `bidsRequested`
   * @param namespace - `home_top` or 'grafana_metrics' etc
   */
  public addPerformanceMetric = (label: string, namespace: string = ''): void | Promise<void> => {
    if (this.isDisabled()) {
      return;
    }

    const metrics = {
      [label]: Math.round(window.performance.now()),
    };

    debugMessage(namespace, `Adding performance metric`, metrics);
    this.sendMetric(metrics);
  };

  /**
   * Used for tracking multiple metrics with custom values
   * @param metrics
   * @param namespace
   * @returns void
   */
  public addMetrics = (metrics: TMetrics, namespace: string = '') => {
    if (this.isDisabled()) {
      return;
    }
    debugMessage(namespace, 'Adding metric', metrics);
    this.sendMetric(metrics);
  };

  /**
   * Used for disabling sending metrics when there is no endpointUrl or virtualUrl
   */

  private isDisabled = (): boolean => {
    return !this.virtualUrl || !this.endpointUrl;
  };

  private onPageLoaded = () => {
    this.pageLoaded = true;
    this.sendQueuedMetrics();
  };

  private postMetrics = async (metrics: TMetrics[]) => {
    let attempts = 0;
    let success = false;
    do {
      try {
        const msg = `Posting metrics to ${this.endpointUrl} endpoint`;

        if (attempts++ > 0) {
          const delayDuration = THROTTLING_THRESHOLD * attempts;
          debugMessage(DEBUG_NAMESPACE, `Retry #${attempts} in ${delayDuration}ms: ${msg}`, metrics);
          await this.delay(delayDuration);
        } else {
          debugMessage(DEBUG_NAMESPACE, msg, metrics);
        }

        const metricsEndpointUrl = `${this.endpointUrl}?${new URLSearchParams({
          platform: PLATFORM_WEB,
        })}`;

        const response = await fetch(metricsEndpointUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            virtualUrl: this.virtualUrl,
            deviceType: isMobile() ? DEVICE_TYPE_MOBILE : DEVICE_TYPE_DESKTOP,
            metrics,
          }),
        });
        // eslint-disable-next-line no-magic-numbers
        if (response.ok || response.status < 400) {
          debugMessage(DEBUG_NAMESPACE, `Metrics sent`, metrics);
          success = true;
        } else {
          debugMessage(DEBUG_NAMESPACE, `Sending metrics endpoint failed`, metrics, response);
        }
      } catch (error) {
        debugMessage(DEBUG_NAMESPACE, `Sending metrics endpoint failed`, metrics, error);
      }
    } while (!success && attempts < RETRY_ATTEMPTS);
    if (!success) {
      throw new Error(`Failed to post metrics`);
    }
  };

  // Adds underscore `_` and suffix to all keys
  private addSuffixToKeys = (metric: TMetrics, suffix: string): TMetrics =>
    Object.entries(metric).reduce((acc, [key, value]) => ({ ...acc, [`${key}_${suffix}`]: value }), {});

  /**
   * Stores metrics in queue and calls sending queued metrics
   */
  private sendMetric = (metric: TMetrics) => {
    if (this.metricsSuffix) {
      this.queue.push(this.addSuffixToKeys(metric, this.metricsSuffix));
    } else {
      this.queue.push(metric);
    }
    this.sendQueuedMetrics();
  };

  /**
   * Sends queued metrics with throttle
   */
  private sendQueuedMetrics = () => {
    if (!this.pageLoaded || this.isDisabled() || this.queue.length === 0 || this.throttlingTimer) {
      return;
    }
    this.throttlingTimer = setTimeout(async () => {
      const metricsToSend = [...this.queue];
      this.queue = [];
      let failed = false;
      try {
        await this.postMetrics(metricsToSend);
      } catch {
        failed = true;
      }
      const hasNewMetrics = this.queue.length > 0;
      if (failed) {
        // store unsent metrics in the queue;
        this.queue.unshift(...metricsToSend);
      }
      // eslint-disable-next-line no-undefined
      this.throttlingTimer = undefined;
      if (hasNewMetrics) {
        // if some events were fired during posting the previous events - start new throttling
        this.sendQueuedMetrics();
      }
    }, THROTTLING_THRESHOLD);
  };

  /**
   * Returns promise with delay
   */
  private delay = (ms: number) => {
    return new Promise((resolve) => setTimeout(resolve, ms));
  };
}
